import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import type {
  AnalysisType as BackendAnalysisType,
  GetUserAnalysesPaginatedQuery,
} from '@/shared/hooks/queries/generated';
import {
  useCreateSearchFolderMutation,
  useDeleteSearchFolderMutation,
  useGetUserSearchFoldersQuery,
  useMoveAnalysisToFolderMutation,
  useMoveAnalysisToPositionMutation,
  useRemoveAnalysisParentMutation,
  useRenameSearchFolderMutation,
  useToggleAnalysisFavoriteMutation,
} from '@/shared/hooks/queries/generated';

// ============================================================================
// Organisation des recherches — VERSION SERVEUR (remplace le store
// localStorage) : dossiers, positions denses, favoris et lineage vivent en
// base, partagés entre sessions/appareils. Le morphing live du drag & drop
// patche le cache react-query (pas d'appel serveur), le drop persiste.
// ============================================================================

export interface SearchFolder {
  id: string;
  name: string;
  position: number;
}

export interface SearchOrganizationActions {
  createFolder: (name: string) => Promise<void>;
  renameFolder: (id: string, name: string) => Promise<void>;
  deleteFolder: (id: string) => Promise<void>;
  /** Morph léger pendant le drag : patch du cache uniquement. */
  morphFolder: (analysisId: string, folderId: string | null) => void;
  /** Persiste le déplacement (dossier + index optionnel dans le dossier). */
  moveSearch: (analysisId: string, folderId: string | null, index?: number) => Promise<void>;
  toggleFavorite: (analysisId: string) => Promise<void>;
  removeParent: (analysisId: string) => Promise<void>;
}

export const useSearchOrganizationServer = (type: BackendAnalysisType) => {
  const queryClient = useQueryClient();

  const { data: foldersData } = useGetUserSearchFoldersQuery({ type }, { staleTime: 5 * 60 * 1000 });
  const folders = useMemo<SearchFolder[]>(
    () =>
      (foldersData?.getUserSearchFolders ?? [])
        .map((folder) => ({ id: folder.id, name: folder.name, position: folder.position }))
        .sort((a, b) => a.position - b.position),
    [foldersData],
  );

  const foldersKey = ['GetUserSearchFolders', { type }];
  const analysesKey = ['GetUserAnalysesPaginated', type];

  /** Patch in-place des analyses paginées (morph dnd + optimistic). */
  const patchAnalyses = (
    updater: (
      analysis: GetUserAnalysesPaginatedQuery['getUserAnalysesPaginated']['analyses'][0],
    ) => GetUserAnalysesPaginatedQuery['getUserAnalysesPaginated']['analyses'][0],
  ) => {
    queryClient.setQueriesData<InfiniteData<GetUserAnalysesPaginatedQuery>>({ queryKey: analysesKey }, (oldData) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map((page) => ({
          ...page,
          getUserAnalysesPaginated: {
            ...page.getUserAnalysesPaginated,
            analyses: page.getUserAnalysesPaginated.analyses.map(updater),
          },
        })),
      };
    });
  };

  const invalidateAll = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: analysesKey }),
      queryClient.invalidateQueries({ queryKey: foldersKey }),
    ]);
  };

  const createFolderMutation = useCreateSearchFolderMutation();
  const renameFolderMutation = useRenameSearchFolderMutation();
  const deleteFolderMutation = useDeleteSearchFolderMutation();
  const moveToFolderMutation = useMoveAnalysisToFolderMutation();
  const moveToPositionMutation = useMoveAnalysisToPositionMutation();
  const toggleFavoriteMutation = useToggleAnalysisFavoriteMutation();
  const removeParentMutation = useRemoveAnalysisParentMutation();

  const actions: SearchOrganizationActions = {
    createFolder: async (name) => {
      await createFolderMutation.mutateAsync({ input: { name, type } });
      await queryClient.invalidateQueries({ queryKey: foldersKey });
    },

    renameFolder: async (id, name) => {
      await renameFolderMutation.mutateAsync({ input: { id, name } });
      await queryClient.invalidateQueries({ queryKey: foldersKey });
    },

    deleteFolder: async (id) => {
      // Optimistic : ses analyses retournent à la racine
      patchAnalyses((analysis) => (analysis.folderId === id ? { ...analysis, folderId: null } : analysis));
      await deleteFolderMutation.mutateAsync({ id });
      await invalidateAll();
    },

    morphFolder: (analysisId, folderId) => {
      patchAnalyses((analysis) => (analysis.id === analysisId ? { ...analysis, folderId } : analysis));
    },

    moveSearch: async (analysisId, folderId, index) => {
      // Un refetch en vol écraserait le patch optimiste — on l'annule d'abord
      await queryClient.cancelQueries({ queryKey: analysesKey });

      // Optimistic COMPLET : dossier + positions denses recalculées pour le
      // dossier cible ET le dossier d'origine. Sans position optimiste, la
      // carte retombait à sa vieille position pendant l'aller-retour serveur
      // puis sautait au refetch (le « rollback » visible au drop).
      queryClient.setQueriesData<InfiniteData<GetUserAnalysesPaginatedQuery>>({ queryKey: analysesKey }, (oldData) => {
        if (!oldData) return oldData;
        const all = oldData.pages.flatMap((page) => page.getUserAnalysesPaginated.analyses);
        const moved = all.find((a) => a.id === analysisId);
        if (!moved) return oldData;

        const byPosition = (a: (typeof all)[number], b: (typeof all)[number]) =>
          (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER);
        const patch = new Map<string, { folderId: string | null; position: number }>();

        // Dossier cible : insertion à l'index demandé (fin de dossier sinon,
        // comme le fait le serveur), puis renumérotation dense
        const targetList = all.filter((a) => (a.folderId ?? null) === folderId && a.id !== analysisId).sort(byPosition);
        const insertAt = index != null ? Math.max(0, Math.min(index, targetList.length)) : targetList.length;
        targetList.splice(insertAt, 0, moved);
        targetList.forEach((a, i) => patch.set(a.id, { folderId, position: i }));

        // Dossier d'origine (si différent) : renumérotation sans la carte
        const originFolderId = moved.folderId ?? null;
        if (originFolderId !== folderId) {
          const originList = all
            .filter((a) => (a.folderId ?? null) === originFolderId && a.id !== analysisId)
            .sort(byPosition);
          originList.forEach((a, i) => patch.set(a.id, { folderId: originFolderId, position: i }));
        }

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            getUserAnalysesPaginated: {
              ...page.getUserAnalysesPaginated,
              analyses: page.getUserAnalysesPaginated.analyses.map((analysis) => {
                const patched = patch.get(analysis.id);
                return patched ? { ...analysis, folderId: patched.folderId, position: patched.position } : analysis;
              }),
            },
          })),
        };
      });

      // Persistance — le refetch final (même en erreur) réaligne sur le serveur
      try {
        await moveToFolderMutation.mutateAsync({ input: { analysisId, folderId } });
        if (index != null) {
          await moveToPositionMutation.mutateAsync({ input: { analysisId, position: index } });
        }
      } finally {
        await queryClient.invalidateQueries({ queryKey: analysesKey });
      }
    },

    toggleFavorite: async (analysisId) => {
      patchAnalyses((analysis) =>
        analysis.id === analysisId ? { ...analysis, isFavorite: !analysis.isFavorite } : analysis,
      );
      await toggleFavoriteMutation.mutateAsync({ analysisId });
      await queryClient.invalidateQueries({ queryKey: analysesKey });
    },

    removeParent: async (analysisId) => {
      patchAnalyses((analysis) => (analysis.id === analysisId ? { ...analysis, parentAnalysisId: null } : analysis));
      await removeParentMutation.mutateAsync({ childId: analysisId });
      await queryClient.invalidateQueries({ queryKey: analysesKey });
    },
  };

  return { folders, actions };
};
